import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  CheckCircle2, Circle, Clock, AlertTriangle, Plus, Calendar, User, Trash2,
  Loader2, ChevronRight, Flag, X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  high: { color: '#DC2626', bg: '#FEF2F2', label: 'High' },
  medium: { color: '#F59E0B', bg: '#FFFBEB', label: 'Medium' },
  low: { color: '#6B7280', bg: '#F3F4F6', label: 'Low' },
};

const TasksPanel = ({ dealId = null, onTaskChange }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('pending'); // pending, completed, all

  const fetchTasks = useCallback(async () => {
    try {
      const endpoint = dealId 
        ? `${API}/api/admin/deals/${dealId}/tasks`
        : `${API}/api/admin/tasks`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setTasks(data.tasks || []);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
    setLoading(false);
  }, [dealId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const createTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim() || !newTask.due_date) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/tasks`, {
        ...newTask,
        deal_id: dealId
      }, { withCredentials: true });
      
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium' });
      setShowAddTask(false);
      fetchTasks();
      if (onTaskChange) onTaskChange();
    } catch (err) {
      console.error('Failed to create task:', err);
    }
    setSubmitting(false);
  };

  const toggleTaskStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API}/api/admin/tasks/${task.id}`, { status: newStatus }, { withCredentials: true });
      fetchTasks();
      if (onTaskChange) onTaskChange();
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/api/admin/tasks/${taskId}`, { withCredentials: true });
      fetchTasks();
      if (onTaskChange) onTaskChange();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === now.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'pending') return t.status !== 'completed';
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '2rem' }}>
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div data-testid="tasks-panel">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['pending', 'completed', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`task-filter-${f}`}
              style={{
                padding: '0.25rem 0.625rem', borderRadius: 4, fontSize: '0.7rem', fontWeight: 500,
                border: filter === f ? '1px solid var(--teal)' : '1px solid #E5E7EB',
                background: filter === f ? 'rgba(13,148,136,0.1)' : '#fff',
                color: filter === f ? 'var(--teal)' : '#6B7280',
                cursor: 'pointer', textTransform: 'capitalize'
              }}
            >
              {f}
            </button>
          ))}
        </div>
        <Button onClick={() => setShowAddTask(true)} className="btn-primary" size="sm" data-testid="add-task-btn">
          <Plus size={14} className="mr-1" /> Add Task
        </Button>
      </div>

      {/* Add Task Form */}
      {showAddTask && (
        <form onSubmit={createTask} style={{
          background: '#FAFBFC', borderRadius: 8, padding: '1rem', border: '1px solid #E5E7EB',
          marginBottom: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h4 style={{ fontSize: '0.813rem', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>New Task</h4>
            <button type="button" onClick={() => setShowAddTask(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
              <X size={16} />
            </button>
          </div>
          
          <Input
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            placeholder="Task title (e.g., Follow up call)"
            data-testid="task-title-input"
            required
            style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)', marginBottom: '0.5rem' }}
          />
          
          <textarea
            value={newTask.description}
            onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            placeholder="Description (optional)"
            rows={2}
            style={{
              width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
              fontSize: '0.875rem', color: 'var(--navy)', resize: 'none', fontFamily: 'var(--font-body)',
              marginBottom: '0.5rem'
            }}
          />
          
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                Due Date
              </label>
              <Input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                data-testid="task-due-date-input"
                required
                style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                Priority
              </label>
              <select
                value={newTask.priority}
                onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                style={{
                  width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
                  fontSize: '0.875rem', color: 'var(--navy)', background: '#fff', height: 40
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          
          <Button type="submit" disabled={submitting} className="btn-primary" data-testid="save-task-btn">
            {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save Task
          </Button>
        </form>
      )}

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: '#9CA3AF', fontSize: '0.813rem' }}>
          {filter === 'completed' ? 'No completed tasks' : 'No tasks yet'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {filteredTasks.map(task => {
            const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
            const isCompleted = task.status === 'completed';
            const isOverdue = task.is_overdue && !isCompleted;
            
            return (
              <div
                key={task.id}
                data-testid={`task-${task.id}`}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.625rem', padding: '0.625rem 0.75rem',
                  background: isOverdue ? '#FEF2F2' : '#fff', borderRadius: 6,
                  border: isOverdue ? '1px solid #FECACA' : '1px solid #E5E7EB',
                  opacity: isCompleted ? 0.6 : 1
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleTaskStatus(task)}
                  data-testid={`task-toggle-${task.id}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }}
                >
                  {isCompleted ? (
                    <CheckCircle2 size={18} style={{ color: '#10B981' }} />
                  ) : (
                    <Circle size={18} style={{ color: isOverdue ? '#DC2626' : '#D1D5DB' }} />
                  )}
                </button>
                
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 500, fontSize: '0.813rem', color: 'var(--navy)',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    marginBottom: '0.125rem'
                  }}>
                    {task.title}
                  </div>
                  
                  {task.description && (
                    <div style={{ fontSize: '0.7rem', color: '#6B7280', marginBottom: '0.25rem' }}>
                      {task.description}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {/* Due date */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      fontSize: '0.65rem', fontWeight: 500,
                      color: isOverdue ? '#DC2626' : '#6B7280'
                    }}>
                      {isOverdue ? <AlertTriangle size={10} /> : <Calendar size={10} />}
                      {formatDueDate(task.due_date)}
                    </span>
                    
                    {/* Priority */}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                      fontSize: '0.6rem', fontWeight: 600, padding: '1px 5px', borderRadius: 100,
                      background: priorityConfig.bg, color: priorityConfig.color
                    }}>
                      <Flag size={8} />
                      {priorityConfig.label}
                    </span>
                  </div>
                </div>
                
                {/* Delete */}
                <button
                  onClick={() => deleteTask(task.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#9CA3AF' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TasksPanel;
